import React, { useState, useMemo, useCallback } from 'react';
import { Plus, X, Video, Search } from 'lucide-react';
import { COR, FONTE } from './ui';
import { CAMADA, authHeaders, modalOverlay, modalCard, modalHeader, btnX, rotuloStyle, inputStyle } from './calendario-ui';

// =====================================================================
// FORMULÁRIO DE EVENTO — modal de criação (POST /api/calendario/eventos)
// =====================================================================
// Morava dentro do CalendarioAniversariantes; saiu de lá quando o modal do ícone
// de calendário passou a criar eventos também. Os dois montam este mesmo modal.
//
// Evento é de dia único ou PERÍODO (o grid pinta cada dia do intervalo). Criar é
// restrito a escritório/gerente (2026-07-16) — quem chama só monta este modal
// quando `opcoes.podeCriar`; a trava de verdade é o 403 da rota.
//
// O que ele controla:
//   - VISIBILIDADE: privado (só o criador) ou compartilhado com PESSOAS. Loja e
//     perfil viraram FILTROS da lista de gente (2026-07-16) — antes eram alvos em
//     si (chips). O filtro leva até a turma e o "Marcar todos" a inclui de uma vez,
//     então o alvo de grupo virou redundante e saiu da tela.
//     ⚠️ O backend SEGUE lendo alvos 'perfil'/'loja': há eventos antigos
//     compartilhados assim, que continuam visíveis para quem casa neles. O que
//     mudou é só o que esta tela PRODUZ. Não remover o suporte no server.
//   - ESPELHO NO TEAMS: cai também na agenda Microsoft do criador, com os
//     compartilhados como convidados. Exige conta vinculada (opcoes.msConectado).
//   - E-MAIL: sai automático do backend para todos os compartilhados.
//
// Props:
//   - ano, mes  : ancoram a data padrão no mês que está sendo exibido
//   - opcoes    : { perfis, lojas, usuarios, podeCriar, msConectado } de GET /api/calendario/opcoes
//   - evento    : (opcional) evento a EDITAR — presente = modo edição (PUT).
//                 Ausente = criação (POST). Vem do GET /eventos (dataInicio,
//                 dataFim, titulo, descricao, compartilhamentos[{tipo,valor}]).
//   - onFechar  : fecha sem salvar
//   - onCriou   : salvou com sucesso (quem chama recarrega a lista). Recebe um
//                 `aviso` quando o portal salvou mas o espelho no Teams falhou.

function FormEvento({ ano, mes, opcoes, evento, onFechar, onCriou }) {
    const editando = !!evento;
    const dataPadrao = `${ano}-${String(mes).padStart(2, '0')}-01`;
    // Prefill a partir do evento (edição) ou do mês exibido (criação).
    const ehPeriodoInicial = !!(evento?.dataFim && evento.dataFim !== evento.dataInicio);
    const alvosIniciais = (evento?.compartilhamentos || []);
    const [data, setData] = useState(evento?.dataInicio || dataPadrao);
    const [periodo, setPeriodo] = useState(ehPeriodoInicial);   // dia único x período
    const [dataFim, setDataFim] = useState(evento?.dataFim || evento?.dataInicio || dataPadrao);
    const [titulo, setTitulo] = useState(evento?.titulo || '');
    const [descricao, setDescricao] = useState(evento?.descricao || '');
    const [modo, setModo] = useState(alvosIniciais.length ? 'compartilhar' : 'privado');   // 'privado' | 'compartilhar'
    const [usuariosSel, setUsuariosSel] = useState(   // IDs (o backend guarda o ID, não o e-mail)
        alvosIniciais.filter((a) => a.tipo === 'usuario').map((a) => Number(a.valor))
    );
    // Alvos de perfil/loja de eventos ANTIGOS: a tela não os produz mais, mas ao
    // EDITAR precisamos preservá-los (senão o PUT, que regrava a lista inteira,
    // apagaria silenciosamente o compartilhamento com a loja/perfil). Só valem
    // enquanto "Compartilhar" — virar "Privado" descompartilha tudo de propósito.
    const alvosLegado = alvosIniciais
        .filter((a) => a.tipo !== 'usuario')
        .map((a) => ({ tipo: a.tipo, valor: String(a.valor) }));
    const [buscaUsuario, setBuscaUsuario] = useState('');
    // Filtros da LISTA de pessoas. Só garimpam quem aparece para escolher — não
    // têm nada a ver com os chips de "Por perfil"/"Por loja" logo abaixo, que são
    // ALVOS (marcar a loja VEL compartilha com a VEL inteira). Filtrar por VEL
    // aqui mostra as pessoas de VEL para você escolher UMA, sem incluir a loja toda.
    const [filtroLoja, setFiltroLoja] = useState('');
    const [filtroPerfil, setFiltroPerfil] = useState('');
    // Espelho no Teams: ligado por padrão, mas só para quem tem conta vinculada —
    // sem vínculo não há em nome de quem criar na Microsoft.
    const [espelharTeams, setEspelharTeams] = useState(!!opcoes.msConectado);
    const [online, setOnline] = useState(false);
    const [salvando, setSalvando] = useState(false);
    const [erro, setErro] = useState('');

    const toggle = (lista, setLista, v) => setLista(lista.includes(v) ? lista.filter((x) => x !== v) : [...lista, v]);

    // Opções dos filtros — tiradas das PESSOAS que vieram, não de uma lista de
    // lojas/perfis à parte. Além de não ter dado morto, evita a armadilha da
    // lista de lojas da dtbCadastroLojas, que EXCLUI o escritório: usá-la aqui
    // deixaria as ~32 pessoas do escritório impossíveis de filtrar. Derivando dos
    // usuários, o filtro sempre reflete exatamente quem está na lista.
    const lojasFiltro = useMemo(
        () => [...new Set((opcoes.usuarios || []).map((u) => u.loja).filter(Boolean))].sort(),
        [opcoes.usuarios]
    );
    const perfisFiltro = useMemo(
        () => [...new Set((opcoes.usuarios || []).map((u) => u.perfil).filter(Boolean))].sort(),
        [opcoes.usuarios]
    );

    // A regra do filtro, escrita UMA vez: a lista exibida e o "Marcar todos"
    // precisam concordar. Se cada um tivesse a sua cópia, um dia divergiriam e o
    // botão marcaria gente que a lista não mostra — silenciosamente.
    const casaFiltro = useCallback((u) => {
        const q = buscaUsuario.trim().toLowerCase();
        return (!filtroLoja || u.loja === filtroLoja)
            && (!filtroPerfil || u.perfil === filtroPerfil)
            && (!q || (u.nome || '').toLowerCase().includes(q));
    }, [buscaUsuario, filtroLoja, filtroPerfil]);

    // Quem o "Marcar todos" alcança: casa no filtro e ainda não está marcado.
    // Sai da lista COMPLETA, não da `usuariosFiltrados` — aquela é cortada no 60
    // para render, então contar por ela faria o botão mentir o número e deixar
    // gente de fora justamente nos grupos grandes, que é onde ele serve.
    const porMarcar = useMemo(
        () => (opcoes.usuarios || []).filter((u) => !usuariosSel.includes(u.id) && casaFiltro(u)),
        [opcoes.usuarios, usuariosSel, casaFiltro]
    );

    // A lista exibida. Quem JÁ está marcado fica sempre no topo, mesmo fora do
    // filtro atual: escolher alguém de VEL e depois filtrar SED não pode dar a
    // impressão de que a escolha se perdeu — nem obrigar a caçar o filtro de
    // volta só para desmarcar.
    const usuariosFiltrados = useMemo(() => {
        const lista = opcoes.usuarios || [];
        const sel = lista.filter((u) => usuariosSel.includes(u.id));
        return [...sel, ...porMarcar].slice(0, 60);   // teto de render; filtro/busca afunilam o resto
    }, [opcoes.usuarios, usuariosSel, porMarcar]);

    const salvar = async () => {
        setErro('');
        if (!titulo.trim()) { setErro('Informe o título do evento.'); return; }
        if (periodo && dataFim < data) { setErro('A data fim não pode ser anterior à data início.'); return; }
        // Alvos do tipo 'usuario' (o que a tela produz) + os legado de perfil/loja
        // preservados na edição. O backend aceita e lê os três tipos.
        const compart = modo === 'privado' ? []
            : [...alvosLegado, ...usuariosSel.map((id) => ({ tipo: 'usuario', valor: String(id) }))];
        if (modo === 'compartilhar' && compart.length === 0) { setErro('Escolha ao menos uma pessoa (ou marque como privado).'); return; }
        setSalvando(true);
        // Corpo comum; a criação leva também o espelho no Teams (na edição, o
        // espelho é atualizado no servidor só se já existir — ver PUT /eventos/:id).
        const corpo = { data, dataFim: periodo ? dataFim : null, titulo, descricao, compartilhamentos: compart };
        if (!editando) {
            corpo.espelharTeams = espelharTeams;
            corpo.online = espelharTeams && online;   // reunião só faz sentido com espelho
        }
        try {
            const r = await fetch(
                editando ? `/api/calendario/eventos/${evento.id}` : '/api/calendario/eventos',
                {
                    method: editando ? 'PUT' : 'POST',
                    headers: { 'Content-Type': 'application/json', ...authHeaders() },
                    body: JSON.stringify(corpo),
                }
            );
            const b = await r.json().catch(() => ({}));
            // O backend responde com `aviso` quando salvou no portal mas o espelho
            // no Teams falhou — salvou, porém não como foi pedido.
            if (r.ok) onCriou(b.aviso || null);
            else setErro(b.message || 'Erro ao salvar evento.');
        } catch (e) { setErro('Erro de conexão.'); } finally { setSalvando(false); }
    };

    const chip = (ativo) => ({ border: `1px solid ${ativo ? CAMADA.evento.cor : COR.bordaSuave}`, backgroundColor: ativo ? CAMADA.evento.cor : '#f8fafc', color: ativo ? '#fff' : '#555', borderRadius: '999px', padding: '4px 10px', fontSize: '12px', fontWeight: 600, cursor: 'pointer', fontFamily: FONTE });
    const subtitulo = { fontSize: '12px', fontWeight: 700, color: '#555', marginBottom: '6px' };
    // Ação em massa: texto, não pastilha — para não competir visualmente com os
    // chips de pessoa, que são a escolha de verdade.
    const btnAcao = { border: `1px solid ${COR.bordaSuave}`, backgroundColor: '#fff', color: CAMADA.evento.cor, borderRadius: '6px', padding: '3px 9px', fontSize: '11px', fontWeight: 700, cursor: 'pointer', fontFamily: FONTE };

    return (
        <div onClick={onFechar} style={modalOverlay}>
            <div onClick={(e) => e.stopPropagation()} style={modalCard}>
                <div style={modalHeader}>
                    <h3 style={{ margin: 0, fontSize: '17px', fontWeight: 700, color: COR.texto }}>{editando ? 'Editar evento' : 'Novo evento'}</h3>
                    <button onClick={onFechar} style={btnX}><X size={20} /></button>
                </div>

                <div style={{ padding: '18px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    {/* Dia único x Período */}
                    <div style={{ ...rotuloStyle, gap: '8px' }}>Quando
                        <div style={{ display: 'flex', gap: '16px', fontWeight: 500, color: COR.texto }}>
                            <label style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
                                <input type="radio" name="quando" checked={!periodo} onChange={() => setPeriodo(false)} /> Dia único
                            </label>
                            <label style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
                                <input type="radio" name="quando" checked={periodo} onChange={() => setPeriodo(true)} /> Período
                            </label>
                        </div>
                    </div>
                    <div style={{ display: 'flex', gap: '10px' }}>
                        <label style={{ ...rotuloStyle, flex: 1 }}>{periodo ? 'Início' : 'Data'}
                            <input type="date" value={data} onChange={(e) => setData(e.target.value)} style={inputStyle} />
                        </label>
                        {periodo && (
                            <label style={{ ...rotuloStyle, flex: 1 }}>Fim
                                <input type="date" value={dataFim} min={data} onChange={(e) => setDataFim(e.target.value)} style={inputStyle} />
                            </label>
                        )}
                    </div>
                    <label style={rotuloStyle}>Título
                        <input type="text" value={titulo} onChange={(e) => setTitulo(e.target.value)} placeholder="Ex.: Reunião de fechamento" style={inputStyle} />
                    </label>
                    <label style={rotuloStyle}>Descrição (opcional)
                        <textarea value={descricao} onChange={(e) => setDescricao(e.target.value)} rows={3} style={{ ...inputStyle, resize: 'vertical' }} />
                    </label>

                    {/* Compartilhamento */}
                    <div style={{ ...rotuloStyle, gap: '8px' }}>Compartilhamento
                        <div style={{ display: 'flex', gap: '16px', fontWeight: 500, color: COR.texto }}>
                            <label style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
                                <input type="radio" name="modo" checked={modo === 'privado'} onChange={() => setModo('privado')} /> Privado (só eu)
                            </label>
                            <label style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
                                <input type="radio" name="modo" checked={modo === 'compartilhar'} onChange={() => setModo('compartilhar')} /> Compartilhar
                            </label>
                        </div>
                    </div>

                    {modo === 'compartilhar' && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', backgroundColor: '#f8fafc', borderRadius: '8px', padding: '12px' }}>
                            <div>
                                <div style={subtitulo}>Com quem</div>
                                {/* Filtros da lista (loja/perfil) + busca por nome. Eles
                                    GARIMPAM a lista; quem compartilha é a escolha da pessoa. */}
                                <div style={{ display: 'flex', gap: '6px', marginBottom: '6px' }}>
                                    <select value={filtroLoja} onChange={(e) => setFiltroLoja(e.target.value)} style={{ ...inputStyle, flex: 1 }}>
                                        <option value="">Todas as lojas</option>
                                        {lojasFiltro.map((l) => <option key={l} value={l}>{l}</option>)}
                                    </select>
                                    <select value={filtroPerfil} onChange={(e) => setFiltroPerfil(e.target.value)} style={{ ...inputStyle, flex: 1 }}>
                                        <option value="">Todos os perfis</option>
                                        {perfisFiltro.map((p) => <option key={p} value={p}>{p}</option>)}
                                    </select>
                                </div>
                                <div style={{ position: 'relative', marginBottom: '8px' }}>
                                    <Search size={14} color="#94a3b8" style={{ position: 'absolute', left: '9px', top: '50%', transform: 'translateY(-50%)' }} />
                                    <input
                                        type="text" value={buscaUsuario} onChange={(e) => setBuscaUsuario(e.target.value)}
                                        placeholder="Buscar por nome…"
                                        style={{ ...inputStyle, paddingLeft: '28px' }}
                                    />
                                </div>
                                {/* Ações em massa. Sem elas, compartilhar com um grupo
                                    inteiro (Gerente Plantao são 37) sairia de um clique
                                    no antigo chip de perfil para 37 cliques — o filtro
                                    acha a turma, o botão a marca de uma vez.
                                    "Marcar" só ADICIONA (nunca desmarca) e some quando
                                    não há ninguém por marcar; desfazer é o "Limpar", que
                                    aparece só quando há o que limpar. */}
                                {(porMarcar.length > 0 || usuariosSel.length > 0) && (
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
                                        {porMarcar.length > 0 && (
                                            <button type="button" onClick={() => setUsuariosSel([...usuariosSel, ...porMarcar.map((u) => u.id)])} style={btnAcao}>
                                                Marcar todos ({porMarcar.length})
                                            </button>
                                        )}
                                        {usuariosSel.length > 0 && (
                                            <button type="button" onClick={() => setUsuariosSel([])} style={{ ...btnAcao, color: '#dc2626', borderColor: '#fecaca' }}>
                                                Limpar ({usuariosSel.length})
                                            </button>
                                        )}
                                    </div>
                                )}
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', maxHeight: '150px', overflowY: 'auto' }}>
                                    {usuariosFiltrados.map((u) => (
                                        <button
                                            key={u.id} type="button"
                                            onClick={() => toggle(usuariosSel, setUsuariosSel, u.id)}
                                            title={`${u.nome} · ${u.perfil || '—'} · ${u.loja || '—'}`}
                                            style={chip(usuariosSel.includes(u.id))}
                                        >
                                            {u.nome}
                                        </button>
                                    ))}
                                    {!usuariosFiltrados.length && (
                                        <span style={{ fontSize: '12px', color: '#94a3b8' }}>Ninguém encontrado.</span>
                                    )}
                                </div>
                            </div>
                            <p style={{ margin: 0, fontSize: '11px', color: '#94a3b8', lineHeight: 1.5 }}>
                                Todos os escolhidos recebem um e-mail com o convite.
                            </p>
                        </div>
                    )}

                    {/* Espelho no Teams — só na CRIAÇÃO. Na edição, o servidor
                        atualiza o espelho automaticamente se ele já existir; criar
                        um espelho novo ao editar fugiria do escopo (v1). */}
                    {!editando && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', borderTop: `1px solid ${COR.bordaSuave}`, paddingTop: '12px' }}>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: opcoes.msConectado ? COR.texto : '#94a3b8', cursor: opcoes.msConectado ? 'pointer' : 'default' }}>
                            <input
                                type="checkbox" checked={espelharTeams} disabled={!opcoes.msConectado}
                                onChange={(e) => setEspelharTeams(e.target.checked)}
                            />
                            <Video size={15} color={opcoes.msConectado ? CAMADA.teams.cor : '#94a3b8'} />
                            Criar também na minha agenda do Teams
                        </label>
                        {!opcoes.msConectado && (
                            <p style={{ margin: '0 0 0 24px', fontSize: '11px', color: '#94a3b8', lineHeight: 1.5 }}>
                                Conecte sua agenda no card <strong>Agenda Corporativa</strong> da Home para espelhar no Teams.
                                O evento continua sendo criado no calendário do portal.
                            </p>
                        )}
                        {espelharTeams && (
                            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginLeft: '24px', fontSize: '13px', color: COR.texto, cursor: 'pointer' }}>
                                <input type="checkbox" checked={online} onChange={(e) => setOnline(e.target.checked)} />
                                Gerar link de reunião do Teams
                            </label>
                        )}
                    </div>
                    )}

                    {erro && <div style={{ color: '#dc2626', fontSize: '12px' }}>{erro}</div>}
                    <button onClick={salvar} disabled={salvando} style={{ alignSelf: 'flex-start', display: 'inline-flex', alignItems: 'center', gap: '6px', backgroundColor: CAMADA.evento.cor, color: '#fff', border: 'none', borderRadius: '8px', padding: '9px 16px', fontFamily: FONTE, fontWeight: 700, fontSize: '13px', cursor: salvando ? 'default' : 'pointer', opacity: salvando ? 0.6 : 1 }}>
                        <Plus size={16} /> {salvando ? 'Salvando…' : (editando ? 'Salvar alterações' : 'Criar evento')}
                    </button>
                </div>
            </div>
        </div>
    );
}

export default FormEvento;
